import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  deletePremiumTokensById,
  getPremiumTokens,
  getPremiumTokensById,
  postPremiumTokens,
  putPremiumTokensById,
  type PremiumToken,
  type PremiumTokenInput,
  type PremiumTokensIndexResponse,
  type PremiumTokenWithOutcomeResponse,
} from 'api/generated'
import { toast } from 'sonner'

interface MembershipCodesState {
  membershipCodes: PremiumToken[]
  membershipCode: PremiumToken | undefined
  loading: boolean
  page: number
  totalPages: number
}

const initialState: MembershipCodesState = {
  membershipCodes: [],
  membershipCode: undefined,
  loading: false,
  page: 1,
  totalPages: 0,
}

export const fetchMembershipCodes = createAsyncThunk<
  PremiumTokensIndexResponse,
  number,
  { rejectValue: string }
>('fetchMembershipCodes', async (page, { rejectWithValue }) => {
  try {
    return await unwrap(getPremiumTokens({ query: { page } }))
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Errore nel caricamento.'))
  }
})

export const createMembershipCode = createAsyncThunk<
  PremiumTokenWithOutcomeResponse,
  PremiumTokenInput,
  { rejectValue: string }
>('createMembershipCode', async (data, { rejectWithValue }) => {
  try {
    return await unwrap(postPremiumTokens({ body: data }))
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Errore nella creazione token'))
  }
})

export const deleteMembershipCode = createAsyncThunk<
  PremiumTokenWithOutcomeResponse,
  number,
  { rejectValue: string }
>('deleteMembershipCode', async (id, { rejectWithValue }) => {
  try {
    return await unwrap(deletePremiumTokensById({ path: { id } }))
  } catch (error) {
    return rejectWithValue(
      errorMessage(error, 'Errore nella cancellazione token')
    )
  }
})

export const showMembershipCode = createAsyncThunk<
  PremiumToken,
  number,
  { rejectValue: string }
>('showMembershipCode', async (id, { rejectWithValue }) => {
  try {
    return await unwrap(getPremiumTokensById({ path: { id } }))
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Errore nel caricamento.'))
  }
})

export const updateMembershipCode = createAsyncThunk<
  PremiumTokenWithOutcomeResponse,
  { id: number; data: PremiumTokenInput },
  { rejectValue: string }
>('updateMembershipCode', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await unwrap(putPremiumTokensById({ path: { id }, body: data }))
  } catch (error) {
    return rejectWithValue(
      errorMessage(error, 'Errore durante aggiornamento token')
    )
  }
})

export const membershipCodesSlice = createSlice({
  name: 'membershipCodes',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchMembershipCodes.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchMembershipCodes.fulfilled, (state, action) => {
      state.loading = false
      state.membershipCodes = action.payload.data
      state.page = action.payload.page
      state.totalPages = action.payload.total_pages
    })
    builder.addCase(fetchMembershipCodes.rejected, (state) => {
      state.loading = false
    })
    builder.addCase(createMembershipCode.fulfilled, (state, action) => {
      state.loading = false
      state.membershipCodes = [
        action.payload.premium_token,
        ...state.membershipCodes,
      ]
      toast.success('Token inserito con successo')
    })
    builder.addCase(createMembershipCode.rejected, (state) => {
      state.loading = false
      toast.error('Errore nella creazione token')
    })
    builder.addCase(deleteMembershipCode.fulfilled, (state, action) => {
      state.loading = false
      state.membershipCodes = state.membershipCodes.filter(
        (membershipCode) =>
          membershipCode.id !== action.payload.premium_token.id
      )
      toast.success('Token cancellato con successo')
    })
    builder.addCase(deleteMembershipCode.rejected, (state) => {
      state.loading = false
      toast.error('Errore nella cancellazione token')
    })
    builder.addCase(showMembershipCode.fulfilled, (state, action) => {
      state.loading = false
      state.membershipCode = action.payload
    })
    builder.addCase(showMembershipCode.rejected, (state) => {
      state.loading = false
    })
    builder.addCase(updateMembershipCode.fulfilled, (state) => {
      state.loading = false
      toast.success('Token aggiornato con successo')
      window.location.href = '/codici-membership'
    })
    builder.addCase(updateMembershipCode.rejected, (state) => {
      state.loading = false
      toast.error('Errore durante aggiornamento token')
    })
  },
})

export const membershipCodesReducer = membershipCodesSlice.reducer
